// Implements: F-CNV-003, F-CNV-004, F-CNV-008, F-CNV-009, F-CNV-013, F-UIX-009
/**
 * The `conversation` module's client, on top of the single HTTP exit.
 *
 * The reads of the thread and of the conversation state, all inside the mockup that
 * owns the thread (`router_conversation.py`). The progress of a turn is NOT a
 * conversation route: it is the existing generation `/stream`, addressed by the
 * `generation_id` the `202` returned. Stopping a turn IS one: `POST .../cancel`.
 *
 * Starting a turn (the third route) is re-exported from `shared/api/startTurn`, which
 * also serves the creation of a mockup; it is never retried (it is paid).
 */

import { conversationPath } from "../../../shared/api/conversationPath";
import { httpRequest, httpStream } from "../../../shared/api/httpClient";
import type {
  CancelAccepted,
  ConversationStateView,
  GenerationDetail,
  MessagesPageView,
  TurnAccepted,
} from "../../../types/api";
import { ApiError, UNKNOWN_ERROR_CODE } from "../../../shared/api/ApiError";
import { API_BASE } from "../../../shared/api/apiBase";

/** Messages per page; the backend's own ceiling is 200. */
export const PAGE_SIZE = 50;

function runPath(mockupId: string, generationId: string): string {
  return `${API_BASE}/mockups/${encodeURIComponent(mockupId)}/generations/${encodeURIComponent(generationId)}`;
}

/** One page of the thread, oldest first; the cursor is opaque and comes from the previous page. */
export async function fetchMessagesPage(
  mockupId: string,
  cursor: string | undefined,
): Promise<MessagesPageView> {
  const query = new URLSearchParams({ limit: String(PAGE_SIZE) });
  if (cursor !== undefined) query.set("cursor", cursor);
  const body = await httpRequest<Partial<MessagesPageView>>(
    `${conversationPath(mockupId)}/messages?${query.toString()}`,
  );
  return { items: body?.items ?? [], next_cursor: body?.next_cursor ?? null };
}

export async function fetchConversationState(mockupId: string): Promise<ConversationStateView> {
  const body = await httpRequest<ConversationStateView>(conversationPath(mockupId));
  if (body === undefined) {
    throw new ApiError(200, UNKNOWN_ERROR_CODE, "GET conversation answered with no body", new Headers());
  }
  return body;
}

export { startTurn } from "../../../shared/api/startTurn";
export type { StartTurnInput } from "../../../shared/api/startTurn";

/** The progress of a turn: the generation stream, one event per change. */
export function streamTurnRun(
  mockupId: string,
  generationId: string,
  signal?: AbortSignal,
): AsyncGenerator<GenerationDetail, void, undefined> {
  return httpStream<GenerationDetail>(`${runPath(mockupId, generationId)}/stream`, { signal });
}

/**
 * Asks the turn the screen follows to stop (`POST .../conversation/cancel`, body `{turn_id}`).
 *
 * `202` is the request being recorded, not the run having stopped. The server cancels only the
 * open turn and only when it is this one: a late or repeated cancel answers `409` instead of
 * stopping somebody else's newer (paid) turn. It is never retried (`retry: false`).
 */
export async function cancelTurn(
  mockupId: string,
  turnId: string,
  signal?: AbortSignal,
): Promise<CancelAccepted | undefined> {
  return httpRequest<CancelAccepted>(`${conversationPath(mockupId)}/cancel`, {
    method: "POST",
    body: { turn_id: turnId },
    signal,
    retry: false,
  });
}

/** The reservation a retry or an edit travels under: the server's idempotency key. */
export interface ReservedTurn {
  readonly turnId: string;
  readonly signal?: AbortSignal | undefined;
}

export interface EditMessageInput extends ReservedTurn {
  readonly text: string;
}

async function acceptedTurn(path: string, body: object, input: ReservedTurn): Promise<TurnAccepted> {
  // Paid and not idempotent from the network's point of view: never retried (`retry: false`),
  // so a lost response reaches the person; the reserved `turn_id` makes a deliberate resend safe.
  const accepted = await httpRequest<TurnAccepted>(path, {
    method: "POST",
    body,
    signal: input.signal,
    retry: false,
  });
  if (accepted === undefined) {
    throw new ApiError(202, UNKNOWN_ERROR_CODE, `POST ${path} answered with no body`, new Headers());
  }
  return accepted;
}

/** Runs the last closed turn again (`POST .../conversation/retry`); the server picks the turn. */
export function retryTurn(mockupId: string, input: ReservedTurn): Promise<TurnAccepted> {
  return acceptedTurn(`${conversationPath(mockupId)}/retry`, { turn_id: input.turnId }, input);
}

export interface ConfirmBriefInput extends ReservedTurn {
  readonly brief: string;
}

/**
 * Draws from the brief the person confirmed or corrected (F-CNV-016):
 * `POST .../conversation/turns/{briefed_turn_id}/brief {brief, turn_id}`. The body's `turn_id`
 * is the idempotency key of the NEW turn; it is paid and never retried.
 */
export function confirmBrief(
  mockupId: string,
  briefedTurnId: string,
  input: ConfirmBriefInput,
): Promise<TurnAccepted> {
  return acceptedTurn(
    `${conversationPath(mockupId)}/turns/${encodeURIComponent(briefedTurnId)}/brief`,
    { brief: input.brief, turn_id: input.turnId },
    input,
  );
}

/** Says an earlier message of the person differently (`POST .../messages/{id}/edit`). */
export function editMessage(
  mockupId: string,
  messageId: string,
  input: EditMessageInput,
): Promise<TurnAccepted> {
  return acceptedTurn(
    `${conversationPath(mockupId)}/messages/${encodeURIComponent(messageId)}/edit`,
    { text: input.text, turn_id: input.turnId },
    input,
  );
}
